import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { TRANSITION_VIDEOS, TRANSITION_FALLBACK_TIMEOUT_MS } from "@/constants/theme";

/**
 * HeroThemeVideo
 * ---------------
 * Renders the cinematic transition video *inside the landing hero background
 * layer* (absolute inset-0), so it sits exactly where the hero background image
 * is and stays perfectly in sync with it. It renders below the hero content
 * (which is only dimmed, not hidden) and above the static background image.
 *
 * It is only mounted by <HeroSection />, and the context only ever sets a
 * `video` transition when the hero is on screen — so the video can never play
 * on another page or when the hero background isn't visible.
 *
 * Lifecycle: play the correct clip -> on end, commit the new theme underneath
 * the last frame (background image swaps invisibly) -> fade the video out to
 * reveal the freshly themed hero.
 */

interface VideoSources {
  webm: string;
  mp4: string;
}

export default function HeroThemeVideo() {
  const { transition, applyTheme, endTransition, setHeroVideoActive } = useTheme();

  const [videoOn, setVideoOn] = useState(false);
  const [srcs, setSrcs] = useState<VideoSources | null>(null);
  const toRef = useRef<boolean>(false);
  const committedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Commit the theme while the last frame still covers the hero, then fade out.
  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    clearTimer();
    applyTheme(toRef.current); // swap the hero background under the last frame
    setVideoOn(false); // triggers the exit fade -> reveal
  }, [applyTheme, clearTimer]);

  // React to a `video` transition request from the context.
  useEffect(() => {
    if (transition?.mode !== "video") return;
    toRef.current = transition.to;
    committedRef.current = false;
    setSrcs(transition.to ? TRANSITION_VIDEOS.toDark : TRANSITION_VIDEOS.toLight);
    setVideoOn(true);
    setHeroVideoActive(true); // dim header + hero content
    clearTimer();
    // Safety net: never let the UI get stuck behind the video.
    timerRef.current = window.setTimeout(commit, TRANSITION_FALLBACK_TIMEOUT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition]);

  // Autoplay as soon as the element mounts; gracefully fall back on failure.
  useEffect(() => {
    if (!videoOn) return;
    const v = videoRef.current;
    if (!v) return;
    const playPromise = v.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => commit());
    }
  }, [videoOn, commit]);

  // Fired once the video overlay has fully faded out.
  const handleExitComplete = useCallback(() => {
    committedRef.current = false;
    endTransition();
  }, [endTransition]);

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {videoOn && srcs && (
        <motion.div
          key="hero-theme-video"
          className="absolute inset-0 z-[5] bg-black overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          data-testid="hero-theme-video-overlay"
          aria-hidden="true"
        >
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            playsInline
            autoPlay
            preload="auto"
            controls={false}
            disablePictureInPicture
            onEnded={commit}
            onError={commit}
            data-testid="hero-theme-video"
          >
            {/* WebM first (preferred), H.264 MP4 fallback for Safari/older browsers */}
            <source src={srcs.webm} type="video/webm" />
            <source src={srcs.mp4} type="video/mp4" />
          </video>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
